if (!customElements.get('print-quote-form')) {
  class PrintQuoteForm extends HTMLElement {
    static allowedExtensions = new Set(['stl', '3mf']);

    constructor() {
      super();
      this.form = this.querySelector('form');
      this.fileInput = this.querySelector('[data-file-input]');
      this.dropzone = this.querySelector('[data-dropzone]');
      this.dropContent = this.querySelector('[data-drop-content]');
      this.fileSummary = this.querySelector('[data-file-summary]');
      this.fileName = this.querySelector('[data-file-name]');
      this.fileMeta = this.querySelector('[data-file-meta]');
      this.removeButton = this.querySelector('[data-file-remove]');
      this.status = this.querySelector('[data-form-status]');
      this.submitButton = this.form?.querySelector('[type="submit"]');
      this.submitLabel = this.submitButton?.textContent || 'Get my free quote';
      this.maxFileMb = this.parseMaxFileMb(this.dataset.maxFileMb);
      this.maxBytes = this.maxFileMb * 1000 * 1000;
      this.endpointConfigured = this.dataset.endpointConfigured === 'true';
      this.eventController = null;
      this.requestController = null;
      this.uploadRequest = null;
      this.trackedQuoteIds = new Set();
    }

    connectedCallback() {
      if (!this.form || !this.fileInput || this.eventController) return;

      this.eventController = new AbortController();
      const listenerOptions = { signal: this.eventController.signal };

      this.fileInput.addEventListener('change', () => {
        this.handleFile(this.fileInput.files?.[0]);
      }, listenerOptions);

      this.removeButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.clearFile();
      }, listenerOptions);

      ['dragenter', 'dragover'].forEach((eventName) => {
        this.dropzone?.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.dropzone.classList.add('is-dragging');
        }, listenerOptions);
      });

      ['dragleave', 'drop'].forEach((eventName) => {
        this.dropzone?.addEventListener(eventName, (event) => {
          event.preventDefault();
          this.dropzone.classList.remove('is-dragging');
        }, listenerOptions);
      });

      this.dropzone?.addEventListener('drop', (event) => {
        this.handleDrop(event);
      }, listenerOptions);

      this.form.addEventListener('submit', (event) => {
        this.handleSubmit(event);
      }, listenerOptions);

      window.addEventListener('pageshow', () => {
        this.restoreSubmitButton();
      }, listenerOptions);
    }

    disconnectedCallback() {
      this.eventController?.abort();
      this.requestController?.abort();
      this.uploadRequest?.abort();
      this.eventController = null;
      this.requestController = null;
      this.uploadRequest = null;
    }

    parseMaxFileMb(value) {
      const parsed = Number.parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 100;
    }

    handleDrop(event) {
      const files = event.dataTransfer?.files;
      if (!files?.length) return;

      if (files.length > 1) {
        this.showStatus('Please upload one file at a time.', 'error');
        return;
      }

      const file = files[0];
      const validationMessage = this.validateFile(file);
      if (validationMessage) {
        this.showStatus(validationMessage, 'error');
        this.clearFile(false);
        return;
      }

      if (typeof DataTransfer !== 'function') {
        this.showStatus('Drag and drop is not supported here. Please use Browse to choose your file.', 'error');
        return;
      }

      const transfer = new DataTransfer();
      transfer.items.add(file);
      this.fileInput.files = transfer.files;
      this.handleFile(file);
    }

    handleFile(file) {
      if (!file) {
        this.clearFile();
        return;
      }

      const validationMessage = this.validateFile(file);
      if (validationMessage) {
        this.showStatus(validationMessage, 'error');
        this.clearFile(false);
        return;
      }

      if (this.fileName) this.fileName.textContent = file.name;
      if (this.fileMeta) this.fileMeta.textContent = this.formatBytes(file.size);
      if (this.dropContent) this.dropContent.hidden = true;
      this.fileSummary?.classList.add('is-visible');
      this.fileSummary?.setAttribute('aria-hidden', 'false');
      this.fileInput.removeAttribute('aria-invalid');
      this.hideStatus();
    }

    validateFile(file) {
      if (!file) return 'Please attach your STL or 3MF file before requesting a quote.';
      if (file.size <= 0) return 'This file is empty. Please choose a valid STL or 3MF file.';

      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!extension || !PrintQuoteForm.allowedExtensions.has(extension)) {
        return 'Please upload an STL or 3MF file.';
      }

      if (file.size > this.maxBytes) {
        return `Your file must be ${this.maxFileMb} MB or smaller.`;
      }

      return '';
    }

    clearFile(resetStatus = true) {
      if (this.fileInput) {
        this.fileInput.value = '';
        this.fileInput.removeAttribute('aria-invalid');
      }
      if (this.dropContent) this.dropContent.hidden = false;
      this.fileSummary?.classList.remove('is-visible');
      this.fileSummary?.setAttribute('aria-hidden', 'true');
      if (this.fileName) this.fileName.textContent = '';
      if (this.fileMeta) this.fileMeta.textContent = '';
      if (resetStatus) this.hideStatus();
    }

    async handleSubmit(event) {
      event.preventDefault();

      const file = this.fileInput.files?.[0];
      const validationMessage = this.validateFile(file);

      if (validationMessage) {
        this.fileInput.setAttribute('aria-invalid', 'true');
        this.showStatus(validationMessage, 'error');
        this.fileInput.focus();
        return;
      }

      if (!this.endpointConfigured || !this.hasSecureEndpoint()) {
        this.showStatus(
          'Preview complete — connect a secure upload endpoint before publishing this form.',
          'success'
        );
        return;
      }

      if (!this.form.reportValidity()) return;

      this.setBusy(true, 'Preparing your upload…');
      this.requestController = new AbortController();
      const { signal } = this.requestController;

      try {
        const quote = await this.createQuote(file, signal);
        await this.uploadFile(file, quote);
        this.trackSuccessfulUpload(quote);

        this.showStatus(
          `Upload complete — reference ${quote.quoteId}. We’ll review your file and email your quote within hours.`,
          'success'
        );
      } catch (error) {
        if (error?.name !== 'AbortError') {
          this.showStatus(
            error?.message || 'Your upload could not be completed. Please try again.',
            'error'
          );
        }
      } finally {
        this.requestController = null;
        this.uploadRequest = null;
        this.setBusy(false);
      }
    }

    async createQuote(file, signal) {
      const formData = new FormData(this.form);
      const response = await fetch(this.form.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.get('email'),
          quantity: formData.get('quantity'),
          color: formData.get('color'),
          material: formData.get('material'),
          notes: formData.get('notes'),
          fileName: file.name,
          fileSize: file.size
        }),
        credentials: 'omit',
        signal
      });

      const payload = await this.readJson(response);
      if (!response.ok) {
        throw new Error(payload?.message || 'We could not start your upload. Please try again.');
      }

      if (!payload?.quoteId || !payload?.uploadUrl || !payload?.clientToken) {
        throw new Error('The upload service returned an invalid response. Please try again.');
      }

      return payload;
    }

    uploadFile(file, quote) {
      return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        this.uploadRequest = request;
        request.open('PUT', quote.uploadUrl, true);
        request.setRequestHeader('Content-Type', quote.uploadContentType || 'application/octet-stream');
        request.setRequestHeader('Authorization', `Bearer ${quote.clientToken}`);
        request.timeout = 15 * 60 * 1000;

        request.upload.addEventListener('progress', (event) => {
          if (!event.lengthComputable) {
            this.showStatus('Uploading your file…', 'progress');
            return;
          }

          const percent = Math.min(100, Math.round((event.loaded / event.total) * 100));
          this.showStatus(`Uploading your file… ${percent}%`, 'progress');
        });

        request.addEventListener('load', () => {
          if (request.status >= 200 && request.status < 300) {
            this.showStatus('Upload complete. Checking your file…', 'progress');
            resolve();
            return;
          }

          reject(new Error('Your file could not be uploaded. Please try again.'));
        });
        request.addEventListener('error', () => reject(new Error('Your connection was interrupted. Please try again.')));
        request.addEventListener('timeout', () => reject(new Error('The upload timed out. Please try again.')));
        request.addEventListener('abort', () => reject(new DOMException('Upload cancelled.', 'AbortError')));
        request.send(file);
      });
    }

    async readJson(response) {
      try {
        return await response.json();
      } catch {
        return null;
      }
    }

    hasSecureEndpoint() {
      try {
        const action = new URL(this.form.getAttribute('action'), window.location.href);
        return action.protocol === 'https:';
      } catch {
        return false;
      }
    }

    restoreSubmitButton() {
      if (!this.submitButton) return;
      this.submitButton.disabled = false;
      this.submitButton.textContent = this.submitLabel;
      this.form?.removeAttribute('aria-busy');
    }

    trackSuccessfulUpload(quote) {
      const quoteId = String(quote?.quoteId || '');
      if (!quoteId || this.trackedQuoteIds.has(quoteId)) return;
      this.trackedQuoteIds.add(quoteId);

      const formData = new FormData(this.form);
      const eventPayload = {
        quote_id: quoteId,
        lead_type: 'custom_print_quote',
        quantity: Number(formData.get('quantity')) || 1,
        color: String(formData.get('color') || ''),
        material: String(formData.get('material') || '')
      };

      // Keep analytics failures from interrupting the customer's success state.
      try {
        window.sfTrack?.('quote_upload_complete', eventPayload);
      } catch (_error) {}

      try {
        window.gtag?.('event', 'generate_lead', eventPayload);
      } catch (_error) {}

      try {
        window.fbq?.(
          'track',
          'Lead',
          {
            content_name: 'Custom 3D print quote upload',
            content_category: 'Custom 3D printing',
            lead_type: eventPayload.lead_type
          },
          { eventID: `custom-print-${quoteId}` }
        );
      } catch (_error) {}
    }

    setBusy(isBusy, label) {
      if (!this.submitButton) return;
      this.submitButton.disabled = isBusy;
      this.submitButton.textContent = isBusy ? label : this.submitLabel;
      if (isBusy) {
        this.form?.setAttribute('aria-busy', 'true');
      } else {
        this.form?.removeAttribute('aria-busy');
      }
    }

    showStatus(message, type) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.className = `print-service__form-status is-visible is-${type}`;
      if (type !== 'progress') this.status.focus({ preventScroll: true });
    }

    hideStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.className = 'print-service__form-status';
    }

    formatBytes(bytes) {
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  customElements.define('print-quote-form', PrintQuoteForm);
}
